import React, { useEffect, useMemo } from 'react';

export default function StarBackground({ imagePath, hideStars, dynamicStars }) {
    useEffect(() => {
        // Only add animation styles if we're using dynamic stars
        if (dynamicStars && !hideStars) {
            const style = document.createElement('style');
            style.innerHTML = `
            @keyframes twinkle {
                0% { opacity: 0.3; }
                50% { opacity: 1; }
                100% { opacity: 0.3; }
            }`;
            document.head.appendChild(style);
        }
    }, [dynamicStars, hideStars]);
    
    const stars = useMemo(() => {
        // Only generate stars if explicitly requested and not hidden
        if (!dynamicStars || hideStars) return [];
        
        const result = [];
        for (let i = 0; i < 1000; i++) {
            const size = Math.random() < 0.8 ? '2px' : '4px';
            const isIcon = Math.random() < 0.1;
            const x = Math.random() * 100;
            const y = Math.random() * 100;
            const delay = Math.random() * 5;
            const color = Math.random() < 0.5 ? '#fff' : '#ffe45c';
            
            result.push({
                            id: i,
                            isIcon,
                            x,
                            y,
                            size,
                            delay,
                            color,
                        });
        }
        return result;
    }, [dynamicStars, hideStars]);

    // Determine which image to show (default or provided)
    // If imagePath is "none", we explicitly want NO image.
    // If no imagePath and no dynamicStars are provided, we use the default background.jpeg
    const effectiveImagePath = imagePath === "none" ? null : (imagePath || (!dynamicStars ? "/assets/images/general/backgroundHQ2.png" : null));

    // Render static image background
    if (effectiveImagePath) {
        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                overflow: 'hidden',
                zIndex: -1,
                pointerEvents: 'none',
            }}>
                <img
                    src={effectiveImagePath}
                    alt="Background"
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        opacity: 0.4,
                    }}
                />
            </div>
        );
    }
    
    // Render dynamic stars on solid background
    return (
        <div style={{
            position: 'fixed',
            backgroundColor: '#1A3751',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            overflow: 'hidden',
            zIndex: -1,
            pointerEvents: 'none',
        }}>
            {stars.map(star => star.isIcon ? (
                <div
                    key={`icon-${star.id}`}
                    style={{
                        position: 'absolute',
                        top: `${star.y}vh`,
                        left: `${star.x}vw`,
                        fontSize: '12px',
                        color: star.color,
                        animation: 'twinkle 5s infinite',
                        animationDelay: `${star.delay}s`,
                        pointerEvents: 'none',
                    }}
                >
                    ✨
                </div>
            ) : (
                <div
                    key={`dot-${star.id}`}
                    style={{
                        position: 'absolute',
                        top: `${star.y}vh`,
                        left: `${star.x}vw`,
                        width: star.size,
                        height: star.size,
                        borderRadius: '50%',
                        backgroundColor: star.color,
                        opacity: 0.8,
                        animation: 'twinkle 10s infinite ease-in-out',
                        animationDelay: `${star.delay}s`,
                        pointerEvents: 'none',
                    }}
                />
            ))}
        </div>
    );
}
